// src/components/auth/LoginForm.jsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../utils/helpers';
import { Mail, Lock, Eye, EyeOff, AlertCircle, ChevronLeft, ArrowLeft } from 'lucide-react';

const LoginForm = ({ onForgotPassword, onSignup }) => {
  const [formData, setFormData] = useState({
    user: '',
    pass: ''
  });
  
  const navigate = useNavigate();
  const [rememberMe, setRememberMe] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const { login, googleLogin, error, clearError } = useAuth();

  const passwordRef = useRef(null);
  const userRef = useRef(null);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          setFormError(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const formatCountdown = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Debug logging
  const log = (message, data = null) => {
    console.log(`[LoginForm] ${message}`, data || '');
  };

const getReturnUrl = () => {
  const saved = sessionStorage.getItem('returnUrl');
  sessionStorage.removeItem('returnUrl');
  // Default to the dashboard — the Dashboard component redirects to
  // complete-profile / select-market only when onboarding is actually needed.
  return saved || '/dashboard';
};

const handleSubmit = async (e) => {
  e.preventDefault();
  log('Form submitted', { username: formData.user.substring(0, 3) + '***' });
  
  if (!formData.user || !formData.pass) {
    showToast('Please fill in all fields', 'error');
    return;
  }

  setIsLoading(true);
  setFormError(null);
  clearError();
  
  try {
    log('Calling login function...');
    const result = await login(formData.user, formData.pass);

      if (result.success) {
        log('Login successful');
        showToast('Login successful!', 'success');
        
        if (window.location.pathname === '/login') {
          navigate(getReturnUrl());
        }
      } else {
        setFormError(result.error || 'Login failed. Please check your credentials.');
        if (result.timeLeft) {
          setCountdown(result.timeLeft);
        }
      }
  } catch (error) {
    log('Unexpected error in handleSubmit', error);
    setFormError('An unexpected error occurred');
  } finally {
    setIsLoading(false);
  }
};

  const handleChange = (e) => {
    setFormError(null);
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Google Login Handler
const handleGoogleLogin = async () => {
  log('Google login initiated');
  setIsLoading(true);
  setFormError(null);
  clearError();
  
  try {
    if (!window.google || !window.google.accounts) {
      const errorMsg = 'Google sign-in is not available. Please try another method.';
      log('Google API not available');
      showToast(errorMsg, 'error');
      setIsLoading(false);
      return;
    }

    const client = window.google.accounts.oauth2.initTokenClient({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      scope: 'https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
      callback: async (response) => {
        if (response.access_token) {
          try {
            log('Google OAuth token received');
            const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v1/userinfo', {
              headers: {
                Authorization: `Bearer ${response.access_token}`
              }
            });
            
            if (!userInfoResponse.ok) {
              throw new Error('Failed to fetch user info from Google');
            }
            
            const userInfo = await userInfoResponse.json();

            log('Google user info fetched', { email: userInfo.email });
            const result = await googleLogin(response.access_token, userInfo.email);

            if (result.success) {
              showToast('Login successful!', 'success');
              
              if (window.location.pathname === '/login') {
                navigate(getReturnUrl());
              }
              
              if (result.requiresPhoneVerification) {
                log('Phone verification required');
              }
            } else {
              log('Google login failed', { error: result.error });
              setFormError(result.error || 'Google login failed');
            }
          } catch (error) {
            log('Google login error in callback', error);
            setFormError('Failed to complete Google login');
          } finally {
            setIsLoading(false);
          }
        } else if (response.error) {
          log('Google OAuth error', response.error);
          setIsLoading(false);
          if (response.error !== 'popup_closed_by_user') {
            let errorMessage = 'Google sign-in was cancelled';
            if (response.error === 'access_denied') {
              errorMessage = 'Google sign-in was denied. Please try again.';
            }
            setFormError(errorMessage);
          }
        }
      }
    });

    log('Requesting Google access token...');
    client.requestAccessToken();

  } catch (error) {
    log('Google login initialization error', error);
    setFormError('Google login failed to initialize');
    setIsLoading(false);
  }
};

  return (
    <div className="w-full mt-3 animate-fade-in">
      {/* Back to Sign Up Arrow - Added here */}
      <button
        type="button"
        onClick={onSignup}
        className="flex items-center gap-2 text-gray-500 hover:text-blue-600 transition-colors mb-4 group"
      >
        <div className="p-1 rounded-full border border-gray-200 group-hover:border-blue-200 group-hover:bg-blue-50 transition-all">
          <ArrowLeft size={18} className="group-hover:text-blue-600" />
        </div>
        <span className="text-sm font-medium">Back to sign up</span>
      </button>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="mb-8">
          <h2 className="text-2xl font-black text-gray-900 mb-2">Sign in to Paymeo</h2>
          <p className="text-gray-500">Enter your credentials to continue.</p>
        </div>

        {/* Display auth context error */}
        {(error || formError) && (
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm animate-fade-in">
            <AlertCircle size={18} className="flex-shrink-0" />
            <span>
              {countdown > 0
                ? `Too many login attempts. Try again in ${formatCountdown(countdown)}`
                : (error || formError)}
            </span>
          </div>
        )}

        {/* Username/Email Field */}
        <div className="space-y-2">
          <label className="text-sm font-bold text-gray-700 ml-1">Phone, email, or username</label>
          <div className="relative group">
            <div className="absolute inset-y-0 left-4 flex items-center text-gray-400 group-focus-within:text-blue-500 transition-colors">
              <Mail size={18} />
            </div>
            <input
              ref={userRef}
              type="text"
              name="user"
              className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-gray-900 transition-all"
              placeholder="e.g. @business or 080..."
              value={formData.user}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
          </div>
        </div>
        
        {/* Password Field */}
        <div className="space-y-2">
          <div className="flex justify-between items-center px-1">
            <label className="text-sm font-bold text-gray-700">Password</label>
            <button
              type="button"
              className="text-xs text-blue-500 font-bold hover:underline"
              onClick={onForgotPassword}
              disabled={isLoading}
            >
              Forgot password?
            </button>
          </div>
          <div className="relative group">
            <div className="absolute inset-y-0 left-4 flex items-center text-gray-400 group-focus-within:text-blue-500 transition-colors">
              <Lock size={18} />
            </div>
            <input
              ref={passwordRef}
              type={showPassword ? "text" : "password"}
              name="pass"
              autoComplete="current-password"
              className="w-full pl-12 pr-12 py-4 bg-gray-50 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none text-gray-900 transition-all"
              placeholder="Your password"
              value={formData.pass}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isLoading}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>
        
        {/* Remember Me Checkbox */}
        {/* <div className="flex items-center">
          <input
            type="checkbox"
            id="flexCheckChecked"
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            disabled={isLoading}
          />
          <label htmlFor="flexCheckChecked" className="ml-2 block text-sm text-gray-700">
            Remember this device
          </label>
        </div> */}

         {/* Google Sign-in Button - Styled like personal */}
        <button
          type="button"
          className="w-full flex items-center justify-between px-6 py-4 border border-gray-200 rounded-2xl hover:bg-gray-50 transition-colors group disabled:opacity-50"
          onClick={handleGoogleLogin}
          disabled={isLoading}
        >
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <img 
                src="https://www.google.com/favicon.ico" 
                alt="Google" 
                className="w-5 h-5"
              />
            </div>
            <div className="text-left">
              <p className="font-bold text-gray-900">Continue with Google</p>
              <p className="text-sm text-gray-500">Quick and secure login</p>
            </div>
          </div>
          <svg className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-4 text-lg bg-blue-600 hover:bg-blue-500 text-white rounded-full shadow-lg shadow-blue-600/20 mt-8 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Signing In...
            </div>
          ) : (
            'Sign in'
          )}
        </button>
        
        {/* Sign Up Link - Keeping this as alternative navigation */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Don't have an account?{' '}
          <button
            type="button"
            className="text-blue-500 font-bold hover:underline"
            onClick={onSignup}
            disabled={isLoading}
          >
            Sign up
          </button>
        </p>
      </form>
    </div>
  );
};

export default LoginForm;